import { inject, Injectable } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { environment } from '../../../environments/environment';
import { SIMRAD_SESSION_IDLE_FALLBACK_MINUTES } from '../../../environments/environment.defaults';
import { SessionExpiryModalComponent } from '../../shared/components/session-expiry-modal/session-expiry-modal.component';
import { AuthService } from './auth.service';

/** Intervalo entre verificações do estado da sessão. */
const TICK_MS = 30_000;
/** Antecedência com que o access token é renovado antes de vencer. */
const RENOVAR_ANTES_MS = 120_000;
/** Antecedência com que o usuário é avisado de que a sessão vai expirar. */
const AVISAR_ANTES_MS = 120_000;
/** Intervalo mínimo entre dois registros de atividade. */
const ATIVIDADE_THROTTLE_MS = 1_000;
/** Falhas de rede consecutivas toleradas antes de encerrar a sessão. */
const MAX_FALHAS_REDE = 2;

/**
 * Eventos que contam como atividade real do usuário. Cobrem mouse, toque, teclado e
 * digitação (`input` pega autocomplete/IME, que não disparam `keydown`).
 */
const EVENTOS_ATIVIDADE = [
  'click',
  'keydown',
  'scroll',
  'pointerdown',
  'touchstart',
  'input',
] as const;

/**
 * Escuta na **fase de captura**: vários componentes da base chamam `stopPropagation()`
 * (modais de análise, `multi-select`, topbar) e um listener de bolha simplesmente não veria
 * o usuário digitando dentro deles. `passive` porque nunca chamamos `preventDefault()`.
 */
const OPCOES_LISTENER: AddEventListenerOptions = { capture: true, passive: true };
/** As opções de remoção precisam casar com `capture`, senão o listener nunca sai. */
const OPCOES_REMOCAO: EventListenerOptions = { capture: true };

/**
 * Dono da sessão (SIMRAD-145).
 *
 * Renova o access token pelo refresh token do Keycloak **somente quando há atividade
 * do usuário**: um timer cego manteria o SSO Session Idle sempre renovado e a sessão
 * viva indefinidamente, inclusive em estação de trabalho compartilhada.
 *
 * Nenhum prazo de sessão é codificado aqui — o instante de fim vem do `exp` do refresh
 * token e, na sua ausência, de `environment.keycloak.sessionIdleFallbackMinutes`.
 */
@Injectable({ providedIn: 'root' })
export class SessionManagerService {
  private readonly authService = inject(AuthService);
  private readonly modalService = inject(NgbModal);

  private intervalo: ReturnType<typeof setInterval> | null = null;
  private ultimaAtividade = Date.now();
  // Zerado de propósito (não `Date.now()`): garante que o primeiro `registrarAtividade()`
  // após `start()` nunca seja descartado pelo throttle, já que `ultimaAtividade` acima é
  // atualizado no mesmo instante em `start()`.
  private ultimoRegistroThrottle = 0;
  private houveAtividade = false;
  private renovacaoEmAndamento: Promise<boolean> | null = null;
  private falhasConsecutivas = 0;
  private encerrando = false;
  private referenciaAviso: NgbModalRef | null = null;

  private readonly aoInteragir = (): void => this.registrarAtividade();
  private readonly aoMudarVisibilidade = (): void => {
    // Cobre a aba que voltou de suspensão com o relógio adiantado.
    // Voltar à aba não conta como atividade — apenas força uma verificação.
    if (document.visibilityState === 'visible') {
      this.verificar();
    }
  };

  start(): void {
    if (this.intervalo !== null) {
      return;
    }
    this.ultimaAtividade = Date.now();
    // Rearma o estado: `start()` pode acontecer depois de um encerramento anterior.
    this.encerrando = false;
    this.houveAtividade = false;
    this.falhasConsecutivas = 0;
    for (const evento of EVENTOS_ATIVIDADE) {
      document.addEventListener(evento, this.aoInteragir, OPCOES_LISTENER);
    }
    document.addEventListener('visibilitychange', this.aoMudarVisibilidade);
    this.intervalo = setInterval(() => this.verificar(), TICK_MS);
  }

  stop(): void {
    if (this.intervalo !== null) {
      clearInterval(this.intervalo);
      this.intervalo = null;
    }
    for (const evento of EVENTOS_ATIVIDADE) {
      document.removeEventListener(evento, this.aoInteragir, OPCOES_REMOCAO);
    }
    document.removeEventListener('visibilitychange', this.aoMudarVisibilidade);
    this.fecharAviso();
  }

  /**
   * Registra atividade real do usuário. `forcar` ignora o throttle — usado pela escolha
   * explícita no aviso de expiração, que não pode depender de o clique ter chegado ao
   * listener do documento.
   */
  registrarAtividade(forcar = false): void {
    const agora = Date.now();
    if (!forcar && agora - this.ultimoRegistroThrottle < ATIVIDADE_THROTTLE_MS) {
      return;
    }
    this.ultimoRegistroThrottle = agora;
    this.ultimaAtividade = agora;
    this.houveAtividade = true;
  }

  /** Renova o token agora. Chamadas concorrentes compartilham a mesma promessa. */
  renovarAgora(): Promise<boolean> {
    if (this.renovacaoEmAndamento) {
      return this.renovacaoEmAndamento;
    }

    // `Promise.resolve().then(...)`: `refreshToken()` pode lançar de forma **síncrona**
    // (a angular-oauth2-oidc valida o tokenEndpoint antes de montar a promessa). Sem isso
    // o erro escaparia de `renovarAgora()` e, no interceptor, substituiria o 401 original.
    this.renovacaoEmAndamento = Promise.resolve()
      .then(() => this.authService.refreshToken())
      .then(() => {
        this.falhasConsecutivas = 0;
        this.houveAtividade = false;
        return true;
      })
      .catch((erro: unknown) => this.tratarFalhaDeRenovacao(erro))
      .finally(() => {
        this.renovacaoEmAndamento = null;
      });

    return this.renovacaoEmAndamento;
  }

  private verificar(): void {
    if (!this.temSessao()) {
      // Ocioso, não desarmado: `start()` roda uma vez por documento, então um teardown aqui
      // (ex.: troca de código no /auth/callback mais lenta que um tick) deixaria a sessão
      // seguinte para sempre sem vigilância — sem aviso e sem corte por inatividade.
      this.fecharAviso();
      return;
    }

    const agora = Date.now();
    const fimDaSessao = this.fimDaSessao();

    if (agora >= fimDaSessao) {
      this.encerrarPorInatividade();
      return;
    }

    if (fimDaSessao - agora <= AVISAR_ANTES_MS) {
      this.abrirAviso(fimDaSessao);
      return;
    }

    // Expiração desconhecida (`null`): não há `expires_at` gravado e nada a antecipar aqui —
    // se ainda houver requisição à API, o 401 e a retentativa do interceptor cuidam do caso.
    const expiracaoAccessToken = this.authService.getAccessTokenExpiration();
    if (
      this.houveAtividade &&
      expiracaoAccessToken !== null &&
      expiracaoAccessToken - agora <= RENOVAR_ANTES_MS
    ) {
      void this.renovarAgora();
    }
  }

  private temSessao(): boolean {
    return this.authService.isAuthenticated() || this.authService.hasRefreshToken();
  }

  /**
   * Instante em que a sessão morre se nada acontecer: o `exp` do refresh token
   * (definido pelo realm) ou, na sua ausência, inatividade medida localmente.
   */
  private fimDaSessao(): number {
    const expRefresh = this.authService.getRefreshTokenExpiration();
    if (expRefresh !== null) {
      return expRefresh;
    }
    const minutos =
      environment.keycloak.sessionIdleFallbackMinutes ?? SIMRAD_SESSION_IDLE_FALLBACK_MINUTES;
    return this.ultimaAtividade + minutos * 60_000;
  }

  private tratarFalhaDeRenovacao(erro: unknown): boolean {
    if (this.ehRefreshTokenInvalido(erro)) {
      this.encerrarPorInatividade();
      return false;
    }
    this.falhasConsecutivas += 1;
    if (this.falhasConsecutivas >= MAX_FALHAS_REDE) {
      this.encerrarPorInatividade();
    }
    return false;
  }

  /**
   * O refresh token foi recusado de fato: `invalid_grant` do Keycloak, ou 400/401 do token
   * endpoint. Outros 4xx (429 de rate limit, 403 de WAF/proxy) não dizem nada sobre o token —
   * esses caem na regra de duas falhas consecutivas, como qualquer problema de rede.
   */
  private ehRefreshTokenInvalido(erro: unknown): boolean {
    const e = erro as { status?: number; error?: { error?: string } } | null;
    if (e?.error?.error === 'invalid_grant') {
      return true;
    }
    return e?.status === 400 || e?.status === 401;
  }

  private encerrarPorInatividade(): void {
    if (this.encerrando) {
      return;
    }
    this.encerrando = true;
    this.stop();
    this.authService.sessionTimeout('inatividade');
  }

  private abrirAviso(fimDaSessao: number): void {
    if (this.referenciaAviso !== null) {
      return;
    }

    const referencia = this.modalService.open(SessionExpiryModalComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
    });
    (referencia.componentInstance as SessionExpiryModalComponent).expiraEm = fimDaSessao;
    this.referenciaAviso = referencia;

    referencia.result.then(
      (resultado: string) => this.tratarResultadoDoAviso(resultado),
      () => this.tratarResultadoDoAviso('expirado')
    );
  }

  private tratarResultadoDoAviso(resultado: string): void {
    if (this.referenciaAviso === null) {
      return;
    }
    this.referenciaAviso = null;

    if (resultado === 'renovar') {
      // A escolha explícita do usuário vale como atividade — e precisa adiar o prazo de
      // inatividade (`ultimaAtividade`), não só liberar a renovação.
      this.registrarAtividade(true);
      void this.renovarAgora();
      return;
    }

    if (resultado === 'sair') {
      // Logout explícito (SSO). Desarma antes: nada mais a vigiar depois do redirect.
      this.stop();
      this.authService.logout();
      return;
    }

    this.encerrarPorInatividade();
  }

  private fecharAviso(): void {
    const referencia = this.referenciaAviso;
    if (referencia === null) {
      return;
    }
    // Zerado antes do dismiss para que o handler de rejeição não reentre aqui.
    this.referenciaAviso = null;
    referencia.dismiss();
  }
}
